import {
  HeaderSwitcher,
  KdsAvatar,
  KdsButton,
  Worklist,
} from "@kazaar-platform/design-system";
import { RiExternalLinkLine, RiMoreFill } from "@remixicon/react";
import { useTranslate } from "@tolgee/react";
import clsx from "clsx";
import { useAtom, useAtomValue } from "jotai";
import { useCallback, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { type WorkItemReadDto } from "@/config/apiClient";
import { FeatureEnum, WorkItemStatusEnum } from "@/config/apiClient.types";
import { getBadgeStatusWorklistText } from "@/helpers/lang/getBadgeStatusWorklistText";
import { isMetadataWithUrl } from "@/helpers/validators/isMetadataWithUrl";
import { useGetIsFeatureActive, useWorklist } from "@/hooks";
import {
  type StructuredServicesByOrganisations,
  useGetStructuredServicesByOrganisation,
} from "@/hooks/useGetStructuredServicesByOrganisation";
import { selectedClientServiceIdAtom, userAtom } from "@/store";
import { ConnectedBreadcrumbs } from "../ConnectedBreadcrumbs";
import { type HeaderProps, type NodeType } from "./Header.types";
import { HeaderWorklist } from "./HeaderWorklist";

export const Header = ({
  avatarUrl,
  logo,
  classnames,
  textClassnames,
  setSelectedClientServiceId: setSelectedClientServiceIdProps,
}: HeaderProps) => {
  // Store
  const userAtomValue = useAtomValue(userAtom);
  const [selectedClientServiceId, setSelectedClientServiceIdAtom] = useAtom(
    selectedClientServiceIdAtom
  );
  const setSelectedClientServiceId =
    setSelectedClientServiceIdProps || setSelectedClientServiceIdAtom;

  const location = useLocation();

  // States
  const [isWorklistOpen, setIsWorklistOpen] = useState(false);
  const [isHeaderSwitcherOpen, setIsHeaderSwitcherOpen] = useState(false);
  const fullName = [userAtomValue?.firstname, userAtomValue?.lastname].join(
    " "
  );
  const isOMS = useMemo<boolean>(
    () => location.pathname.includes("/oms"),
    [location.pathname]
  );

  // Hooks
  const { t } = useTranslate();
  const { useGetInfiniteWorklist } = useWorklist();
  const { data, isLoading, isFetching, fetchNextPage } =
    useGetInfiniteWorklist();
  const observer = useRef<IntersectionObserver>(null);
  const navigate = useNavigate();
  const { isActive: isReportingFeatureActive, metadata } =
    useGetIsFeatureActive(FeatureEnum.REPORTING);

  // Redefine Variables
  const totalNumberOfActions = useMemo(
    () => data?.pages?.at(0)?.data?.total ?? 0,
    [data?.pages]
  );
  const worklistItems: WorkItemReadDto[] = useMemo(
    () =>
      (data?.pages || []).reduce((acc: WorkItemReadDto[], currentPage) => {
        const pageWorklist: WorkItemReadDto[] =
          currentPage.data?.worklist || [];
        return acc.concat(pageWorklist);
      }, []),
    [data?.pages]
  );
  const serviceName = useMemo(
    () =>
      userAtomValue?.Services?.find((o) => o.id === selectedClientServiceId)
        ?.name || "",
    [userAtomValue?.Services, selectedClientServiceId]
  );

  const { structuredServicesByOrganisations } =
    useGetStructuredServicesByOrganisation();

  const lastElementRef = useCallback(
    (node: HTMLLIElement) => {
      if (isLoading) {
        return;
      }

      if (observer.current) {
        observer.current.disconnect();
      }

      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          !isFetching &&
          worklistItems.length < totalNumberOfActions
        ) {
          void fetchNextPage();
        }
      });

      if (node) {
        observer.current.observe(node);
      }
    },
    [
      fetchNextPage,
      isFetching,
      isLoading,
      worklistItems.length,
      totalNumberOfActions,
    ]
  );

  const handleSelectedClientServiceOrApplication = (
    organisationIdOrApplication: number
  ) => {
    if (isOMS) {
      void navigate("/oms");
    } else {
      void navigate("/");
    }
    setSelectedClientServiceId(organisationIdOrApplication);
  };

  const switcherMapper = (
    nodes:
      | Array<StructuredServicesByOrganisations[number]["services"][number]>
      | StructuredServicesByOrganisations[number]["services"][number]
  ): NodeType["nodes"] => {
    if (!nodes) {
      return [];
    }
    if (Array.isArray(nodes)) {
      return nodes.map((node) => ({
        value: node.id,
        label: node.name,
        nodes: switcherMapper(node.ChildServices),
      }));
    }
    return [
      {
        value: nodes.id,
        label: nodes.name,
        nodes: switcherMapper(nodes.ChildServices),
      },
    ];
  };

  const headerTree = structuredServicesByOrganisations.map((org) => ({
    id: org.organisation.id,
    label: org.organisation.name,
    tree: switcherMapper(org.services),
  }));

  const handleReportingOnPress = useCallback(() => {
    if (!isMetadataWithUrl(metadata)) {
      return;
    }

    window.open(metadata.url, "_blank");
  }, [metadata]);

  return (
    <div
      data-testid="ok"
      className={clsx(
        "relative h-[100px] w-screen border-b border-slate-200 sm:!h-[60px]",
        classnames
      )}
    >
      <header className="flex h-full w-full flex-wrap items-center justify-between overflow-hidden pb-1 pr-6 sm:!flex-nowrap sm:!pb-0">
        <div className="flex w-full items-center justify-center sm:!w-fit">
          <div className="w-[236px] border-r border-r-outlined-border">
            <HeaderSwitcher
              isOpen={isHeaderSwitcherOpen}
              setIsOpen={setIsHeaderSwitcherOpen}
              variant="light"
              name={serviceName}
              logo={logo}
              selected={selectedClientServiceId}
              onChange={handleSelectedClientServiceOrApplication}
              tree={headerTree}
            />
          </div>

          <div className="pl-sp-md">
            <ConnectedBreadcrumbs className="ml-4 hidden lg:flex" />
          </div>
        </div>
        <div className="flex w-full items-center justify-between gap-2 sm:!w-fit sm:!justify-end md:!gap-8">
          {!!data && (
            <HeaderWorklist
              isOpen={isWorklistOpen}
              setIsOpen={setIsWorklistOpen}
              nbOfActions={totalNumberOfActions}
              textClassnames={textClassnames}
            />
          )}

          {isReportingFeatureActive && (
            <KdsButton
              variant="outlined"
              iconRight={RiExternalLinkLine}
              color="neutral"
              onPress={handleReportingOnPress}
            >
              {t("header.reportingButtonLabel")}
            </KdsButton>
          )}

          <Link to={isOMS ? "/oms/account" : "/account"}>
            <KdsAvatar size="sm" src={avatarUrl} type="image" name={fullName} />
          </Link>
        </div>
      </header>

      <Worklist
        title={t("worklist.title")}
        subTitle={t("worklist.subTitle")}
        isVisible={isWorklistOpen}
        emptyMessage={t("worklist.emptyMessage")}
        emptyIcon={t("worklist.emptyIcon")}
        setIsVisible={setIsWorklistOpen}
      >
        {worklistItems?.map((item, index) => {
          const isLast = worklistItems.length === index + 1;

          const isRetroPlanningStatus =
            item.status === WorkItemStatusEnum.RETROPLANNING_TO_FILL ||
            item.status === WorkItemStatusEnum.RETROPLANNING_SHARED;

          const link = isRetroPlanningStatus
            ? `/orders/${item.orderId}?tab=retroplanning&clientServiceId=${selectedClientServiceId}`
            : `/orders/${item.orderId}/order-items/${item.orderItemId}`;

          const getSubtitle = () => {
            if (isRetroPlanningStatus) {
              return `${item.referenceId}`;
            }
            if (item.name) {
              return `${item.referenceId} . ${item.name}`;
            }
            return "";
          };
          const subtitle = getSubtitle();

          return (
            <Worklist.Item
              key={item.id}
              {...(isLast ? { ref: lastElementRef } : {})}
              ref={lastElementRef}
              name={item.orderName}
              subTitle={subtitle}
              orderId={item.orderId}
              orderItemId={item.orderItemId}
              className={isLast ? "mb-32" : ""}
              label={getBadgeStatusWorklistText(item.status)}
              LinkComponent={(linkComponentProps) =>
                !isOMS && userAtomValue?.role === "CSM" ? (
                  <a
                    href={`/oms${link}`}
                    tabIndex={!isWorklistOpen ? -1 : undefined}
                  >
                    {linkComponentProps.children}
                  </a>
                ) : (
                  <Link
                    to={isOMS ? `/oms${link}` : link}
                    onClick={() => setIsWorklistOpen(false)}
                    tabIndex={!isWorklistOpen ? -1 : undefined}
                  >
                    {linkComponentProps.children}
                  </Link>
                )
              }
            />
          );
        })}

        {isLoading && (
          <div className="my-5 flex w-full items-center justify-center">
            <RiMoreFill className="h-5 w-5 animate-ping text-inherit" />
          </div>
        )}
      </Worklist>
    </div>
  );
};
